import React from 'react';
import {
  PACK_DURATION,
  PACKAGE_CODE,
  PACKAGE_EXPIRED,
  PACKAGE_LEVEL,
  PACKAGE_LEVEL_V3,
} from '@src/constants/package';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

const PackagePrice = ({ pkg, pricing, discounts = {}, flags = {} }) => {
  const { t, i18n } = useTranslation();
  const { language } = i18n;

  const { price = 0, totalPrice = 0, originalPrice = 0 } = pricing || {};

  const { isDiscountPackage = false, shouldShowDiscount = false } = discounts;

  const {
    isVietNam = false,
    isMobile = false,
    showPackageTotalPrice = false,
  } = flags;

  const currencySymbol = isVietNam ? 'đ' : '$';
  const isEnterpriseApi = pkg.code === PACKAGE_CODE.API_ENTERPRISE_YEAR_V2;
  const isCustomPackage = pkg.level === PACKAGE_LEVEL_V3.CUSTOM;

  const getBilledDesc = (duration) => {
    const isPackageYear =
      duration === PACK_DURATION.YEAR || duration === PACKAGE_EXPIRED.YEARLY;
    const isPackageMonth = duration === PACK_DURATION.MONTH;
    if (isPackageMonth) return t('billedMonthly');
    if (isPackageYear) return t('perYear');
    return '';
  };

  const TotalPriceDisplay = ({ value }) => (
    <>
      <Typography
        className={classNames('original-price', {
          'price-en': !isVietNam && value > 0,
        })}
      >
        <span
          className={classNames('original-price-unit', 'font-size-14', {
            underlined: isVietNam,
          })}
        >
          {currencySymbol}
        </span>
        {value}
      </Typography>
      <Typography className="sub-origin-price">
        {getBilledDesc(pkg.expiresIn)}
      </Typography>
    </>
  );

  const getPriceUnit = () => {
    if (isCustomPackage) return `/${t('year')}`;
    return `/${t('month')}`;
  };

  const renderInternationalPrice = (formattedPrice, priceValue) => {
    if (isEnterpriseApi) {
      return (
        <span className="three-first">
          {priceValue.toLocaleString(language)}
        </span>
      );
    }
    if (isVietNam) {
      const mainPart = formattedPrice.slice(0, -4) || '0';
      const subPart =
        formattedPrice.slice(-4) === '0' ? '' : formattedPrice.slice(-4);
      return (
        <>
          <span className="three-first">{mainPart}</span>
          <span className="three-last">{subPart}</span>
        </>
      );
    }
    return formattedPrice;
  };

  const PriceDisplay = ({ priceValue }) => {
    const formattedPrice = priceValue.toLocaleString(language);
    const priceClassName = classNames(
      isDiscountPackage || pkg.mostPopular ? 'price-highlight' : 'price',
      { 'price-en': !isVietNam && priceValue > 0 },
      { 'unit-price-vip': pkg?.level === PACKAGE_LEVEL.VIP },
    );
    const unitPriceClassName = classNames({
      'unit-price-highlight': isDiscountPackage || pkg.mostPopular,
      'unit-price': !(isDiscountPackage || pkg.mostPopular),
      underlined: isVietNam,
    });
    return (
      <Typography className={priceClassName}>
        <span className={unitPriceClassName}>{currencySymbol}</span>
        {renderInternationalPrice(formattedPrice, priceValue)}
      </Typography>
    );
  };
  const UnitPrice = () => (
    <span className="original-price-not-line-through">
      &nbsp;{getPriceUnit()}
    </span>
  );

  const OriginalPrice = () => {
    if (shouldShowDiscount)
      return (
        <>
          <div className="price-original-discount">
            <Typography>
              <span
                className={classNames('discount-unit-price', {
                  underlined: isVietNam,
                })}
              >
                {currencySymbol}
              </span>
              <span className="original-price-month">
                {originalPrice.toLocaleString(language)}
              </span>
            </Typography>
          </div>
          <UnitPrice />
        </>
      );
    return null;
  };

  const packageClassName = classNames('price-package', {
    'price-package-vip': pkg?.level === PACKAGE_LEVEL.VIP,
  });

  return (
    <>
      <div className="price-wrapper">
        <div className={packageClassName}>
          <PriceDisplay priceValue={price} />
        </div>
        {isCustomPackage && <UnitPrice />}
        {!isCustomPackage && <OriginalPrice />}
      </div>
      {isCustomPackage ? (
        <div className="original-custom-price-wrapper">
          <OriginalPrice />
        </div>
      ) : (
        showPackageTotalPrice && (
          <div className="original-price-wrapper">
            <TotalPriceDisplay value={totalPrice.toLocaleString(language)} />
          </div>
        )
      )}
      {!isMobile && !showPackageTotalPrice && !isCustomPackage && (
        <div className="original-price-wrapper" />
      )}
    </>
  );
};

export default PackagePrice;
